"use client";

import * as React from "react";
import styled, { css } from "styled-components";
import transition from "../../utils/transition";
import Text from "../../Text";
import defaultTheme from "../../defaultTheme";
import media from "../../utils/mediaQuery";
import { StyledModalSection } from "../ModalSection";
import { left, right, rtlSpacing } from "../../utils/rtl";
import { ModalContext } from "../ModalContext";
import useModalContextFunctions from "../helpers/useModalContextFunctions";
export const ModalHeading = styled.h2.withConfig({
  displayName: "ModalHeader__ModalHeading",
  componentId: "sc-ay6mpc-0"
})(["", ""], ({
  theme
}) => css(["margin:0;font-size:", ";font-weight:", ";line-height:", ";color:", ";", ";"], theme.orbit.fontSizeHeadingTitle2, theme.orbit.fontWeightHeadingTitle2, theme.orbit.lineHeightHeadingTitle2, theme.orbit.colorHeading, media.largeMobile(css(["font-size:", ";font-weight:", ";line-height:", ";"], theme.orbit.fontSizeHeadingTitle1, theme.orbit.fontWeightHeadingTitle1, theme.orbit.lineHeightHeadingTitle1))));
ModalHeading.defaultProps = {
  theme: defaultTheme
};

// TODO: create token marginModalTitle and marginModalTitleWithIllustration
const ModalTitle = styled.div.withConfig({
  displayName: "ModalHeader__ModalTitle",
  componentId: "sc-ay6mpc-1"
})(["", ""], ({
  theme,
  illustration
}) => css(["margin-top:", ";", "{padding-", ":", ";}", ";"], illustration && theme.orbit.spaceMedium, ModalHeading, right, theme.orbit.spaceXLarge, media.desktop(css(["", "{padding:0;}"], ModalHeading))));
ModalTitle.defaultProps = {
  theme: defaultTheme
};
const ModalDescription = styled.div.withConfig({
  displayName: "ModalHeader__ModalDescription",
  componentId: "sc-ay6mpc-2"
})(["margin-top:", ";"], ({
  theme
}) => theme.orbit.spaceXSmall);
ModalDescription.defaultProps = {
  theme: defaultTheme
};
const getModalHeaderPadding = (desktop = false) => ({
  theme,
  suppressed
}) => {
  if (desktop) {
    if (suppressed) {
      return theme.orbit.spaceXLarge;
    }
    return `${theme.orbit.spaceXLarge} ${theme.orbit.spaceXLarge} 0 ${theme.orbit.spaceXLarge}`;
  }
  if (suppressed) {
    return `${theme.orbit.spaceXLarge} ${theme.orbit.spaceMedium}`;
  }
  return `${theme.orbit.spaceLarge} ${theme.orbit.spaceMedium} 0 ${theme.orbit.spaceMedium}`;
};
export const StyledModalHeader = styled.div.withConfig({
  displayName: "ModalHeader__StyledModalHeader",
  componentId: "sc-ay6mpc-3"
})(["", ""], ({
  theme,
  suppressed,
  isMobileFullPage
}) => css(["width:100%;display:block;padding:", ";border-top-left-radius:", ";border-top-right-radius:", ";box-sizing:border-box;background-color:", ";& ~ ", ":first-of-type{border-top:", ";border-top-left-radius:0;border-top-right-radius:0;margin-top:", ";}", ";"], rtlSpacing(getModalHeaderPadding()({
  theme,
  suppressed
})), !isMobileFullPage && "12px", !isMobileFullPage && "12px", suppressed ? theme.orbit.paletteCloudLight : theme.orbit.paletteWhite, StyledModalSection, suppressed && `1px solid ${theme.orbit.paletteCloudNormal}`, suppressed && "0!important", media.largeMobile(css(["padding:", ";& ~ ", ":first-of-type{border-top-left-radius:0;border-top-right-radius:0;}"], rtlSpacing(getModalHeaderPadding(true)({
  theme,
  suppressed
})), StyledModalSection))));
StyledModalHeader.defaultProps = {
  theme: defaultTheme
};
export const MobileHeader = styled.div.withConfig({
  displayName: "ModalHeader__MobileHeader",
  componentId: "sc-ay6mpc-4"
})(["", ""], ({
  theme,
  isMobileFullPage
}) => css(["display:inline-block;position:fixed;visibility:hidden;height:52px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;top:", ";", ":48px;", ":0;font-family:", ";font-weight:", ";font-size:18px;color:", ";line-height:52px;box-sizing:border-box;padding:", ";opacity:0;transition:", ";z-index:800;", ";"], isMobileFullPage ? "0" : "16px", right, left, theme.orbit.fontFamily, theme.orbit.fontWeightHeadingDisplay, theme.orbit.colorHeading, rtlSpacing(`0 0 0 ${theme.orbit.spaceLarge}`), transition(["top", "opacity", "visibility"], "fast", "ease-in-out"), media.largeMobile(css(["left:auto;right:auto;padding:0;"]))));
MobileHeader.defaultProps = {
  theme: defaultTheme
};
const getModalHeaderContentMargin = ({
  hasHeader,
  hasDescription,
  hasChildren
}) => {
  if (!hasHeader && hasChildren) return "0";
  return hasDescription ? "32px" : "16px";
};
const StyledModalHeaderContent = styled.div.withConfig({
  displayName: "ModalHeader__StyledModalHeaderContent",
  componentId: "sc-ay6mpc-5"
})(["margin-top:", ";"], ({
  hasHeader,
  hasDescription,
  hasChildren
}) => getModalHeaderContentMargin({
  hasHeader,
  hasDescription,
  hasChildren
}));
const ModalHeader = ({
  illustration,
  suppressed,
  children,
  description,
  title,
  dataTest
}) => {
  const {
    setHasModalTitle,
    hasMobileHeader,
    isMobileFullPage,
    titleID
  } = React.useContext(ModalContext);
  useModalContextFunctions();
  React.useEffect(() => {
    if (title) setHasModalTitle?.(true);
    return () => {
      setHasModalTitle?.(false);
    };
  }, [title, setHasModalTitle]);
  const hasHeader = title || description;
  return /*#__PURE__*/React.createElement(StyledModalHeader, {
    illustration: !!illustration,
    suppressed: suppressed,
    "data-test": dataTest,
    isMobileFullPage: isMobileFullPage
  }, illustration, hasHeader && /*#__PURE__*/React.createElement(ModalTitle, {
    illustration: !!illustration
  }, title && /*#__PURE__*/React.createElement(ModalHeading, {
    id: titleID
  }, title), description && /*#__PURE__*/React.createElement(ModalDescription, null, /*#__PURE__*/React.createElement(Text, {
    size: "large",
    as: "div"
  }, description))), children && /*#__PURE__*/React.createElement(StyledModalHeaderContent, {
    hasHeader: !!hasHeader,
    hasChildren: !!children,
    hasDescription: !!description
  }, children), title && hasMobileHeader && /*#__PURE__*/React.createElement(MobileHeader, {
    role: "presentation",
    isMobileFullPage: isMobileFullPage
  }, title));
};
export default ModalHeader;